import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Button, TextField, Typography } from '@mui/material'
import {
  PanoramaFishEye,
  Park,
  WaterDrop,
  Pets,
  DeleteSweep,
  ArrowDownward,
  Water,
  Grass,
  Help,
} from '@mui/icons-material'
import SvgIcon from '@mui/material/SvgIcon'
import { mdiPipe } from '@mdi/js'
import { t } from 'i18next'
import { closeModal } from 'actions/app'
import { deleteMarker, updateMarkerNote } from 'api/marker'
import { fetchOperators } from 'api/operator'
import { getUserList } from 'api/user'
import ModalContainer from 'components/modal/modal-container'
import ModalHeader from 'components/modal/modal-header'
import ModalActions from 'components/modal/modal-actions'
import { TEST_IDS } from '../../../test-ids'
import type { Marker } from 'store/state/marker'
import type { AppState } from 'state/index'

const Pipe = (props: import('@mui/material/SvgIcon').SvgIconProps) => (
  <SvgIcon {...props}>
    <path d={mdiPipe} />
  </SvgIcon>
)

const ICONS: Record<string, React.ElementType> = {
  PanoramaFishEye,
  Park,
  WaterDrop,
  Pets,
  Pipe,
  DeleteSweep,
  ArrowDownward,
  Water,
  Grass,
  Help,
}

type Props = {
  marker: Marker
}

export default function MarkerDialog({ marker }: Props) {
  const dispatch = useDispatch()
  const users = useSelector((state: AppState) => state.account.userList)
  const currentUser = useSelector((state: AppState) => state.account.user)
  const operators = useSelector(
    (state: AppState) => state.account.operatorsList,
  )

  useEffect(() => {
    if (!users || users.length === 0) {
      dispatch(getUserList() as any)
    }
    if (!operators || operators.length === 0) {
      dispatch(fetchOperators() as any)
    }
  }, [dispatch, users?.length, operators?.length])

  const [note, setNote] = useState(marker.note ?? '')
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [saving, setSaving] = useState(false)

  const handleClose = () => {
    dispatch(closeModal())
  }

  const handleSave = () => {
    setSaving(true)
    dispatch(updateMarkerNote(marker.id, note) as any).finally(() => {
      setSaving(false)
      dispatch(closeModal())
    })
  }

  const handleDelete = () => {
    dispatch(deleteMarker(marker.id) as any)
    dispatch(closeModal())
  }

  const timestamp = marker.timestamp
    ? new Date(marker.timestamp).toLocaleString()
    : null

  const currentUserTypeChecked = currentUser as any
  const createdByUser =
    users?.find((u) => u.id === marker.createdByUserId) ??
    (currentUserTypeChecked?.id === marker.createdByUserId
      ? currentUserTypeChecked
      : undefined)
  const createdByOperator = operators?.find(
    (o) => o.id === marker.createdByOperatorId,
  )

  const creatorName = createdByOperator?.name ?? createdByUser?.fullName

  const Icon = marker.markerType?.iconName
    ? ICONS[marker.markerType.iconName]
    : undefined

  console.log('MarkerDialog creator', { marker })
  if (confirmingDelete) {
    return (
      <ModalContainer>
        <ModalHeader>{t('delete_marker')}</ModalHeader>
        <Box
          data-testid={TEST_IDS.markerDialogConfirmDelete}
          sx={{
            p: 2,
          }}
        >
          <Typography>{t('delete_marker_warning')}</Typography>
        </Box>
        <ModalActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setConfirmingDelete(false)}
          >
            {t('cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            {t('delete_marker')}
          </Button>
        </ModalActions>
      </ModalContainer>
    )
  }

  return (
    <ModalContainer>
      <ModalHeader>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {Icon && <Icon />}
          {marker.markerType
            ? t(marker.markerType.name, {
                defaultValue: marker.markerType.name,
              })
            : t('marker_details')}
        </Box>
      </ModalHeader>
      <Box
        data-testid={TEST_IDS.markerDialog}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 400,
          py: 2,
          gap: 2,
        }}
      >
        {timestamp && (
          <Typography variant="body2">
            {t('marker_timestamp')}: {timestamp}
          </Typography>
        )}
        {creatorName && (
          <Typography variant="body2">
            {t('marker_created_by')}: {creatorName}
          </Typography>
        )}
        <TextField
          label={t('marker_note')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          rows={3}
          fullWidth
          sx={{ width: '100%' }}
          slotProps={{
            htmlInput: { 'data-testid': TEST_IDS.markerDialogNoteInput },
          }}
        />
      </Box>
      <ModalActions>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setConfirmingDelete(true)}
          data-testid={TEST_IDS.markerDialogDeleteButton}
        >
          {t('delete_marker')}
        </Button>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={saving}
          onClick={handleSave}
        >
          {t('apply')}
        </Button>
      </ModalActions>
    </ModalContainer>
  )
}
